import React from 'react';
import { useAuthContext } from '../../auth';
import { useAccessControl } from '../../hooks/useAccessControl';
import './nav.css';

interface MainNavProps {
  onNavigate?: (route: string) => void;
  onLogout?: () => void;
}

export const MainNavWeb = ({ onNavigate, onLogout }: MainNavProps) => {
  const { user, logout } = useAuthContext();
  const { isAdmin } = useAccessControl();
  const [openMenu, setOpenMenu] = React.useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleNavigation = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
    } else if (typeof window !== 'undefined') {
      window.location.href = href;
    }
    setOpenMenu(null);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      if (onLogout) {
        onLogout();
      }
      handleNavigation('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <nav className="nav-root">
      <div className="nav-container">
        {/* Logo */}
        <button className="nav-brand" onClick={() => handleNavigation('/')}>
          <span className="nav-logo">TaskMaster</span>
        </button>

        {/* Mobile Menu Toggle */}
        <button 
          className="nav-mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`nav-hamburger ${mobileMenuOpen ? 'active' : ''}`}></span>
        </button>

        {/* Navigation Links */}
        <div className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <div className="nav-links">
            <button
              className="nav-link"
              onClick={() => handleNavigation('/')}
            >
              Home
            </button>

            <button
              className="nav-link"
              onClick={() => handleNavigation('/blog')}
            >
              Blog
            </button>

            {user && (
              <button
                className="nav-link"
                onClick={() => handleNavigation('/profile')}
              >
                Profile
              </button>
            )}

            {user && (
              <button
                className="nav-link"
                onClick={() => handleNavigation('/jobs')}
              >
                Job Queue
              </button>
            )}

            {isAdmin && (
              <button
                className="nav-link"
                onClick={() => handleNavigation('/files')}
              >
                Files
              </button>
            )}

            {isAdmin && (
              <div className="nav-dropdown">
                <button
                  className="nav-link nav-link-dropdown"
                  onClick={() => setOpenMenu(openMenu === 'admin' ? null : 'admin')}
                >
                  Admin
                  <svg className={`nav-chevron ${openMenu === 'admin' ? 'open' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {openMenu === 'admin' && (
                  <div className="nav-dropdown-menu">
                    <button
                      onClick={() => handleNavigation('/admin')}
                      className="nav-dropdown-item"
                    >
                      Admin Panel
                    </button>
                    <button
                      onClick={() => handleNavigation('/admin-debug')}
                      className="nav-dropdown-item"
                    >
                      Debug Tools
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Section */}
          <div className="nav-user-section">
            {user ? (
              <div className="nav-user">
                <button
                  className="nav-user-button"
                  onClick={() => setOpenMenu(openMenu === 'user' ? null : 'user')}
                >
                  <span className="nav-user-avatar">{user.email?.charAt(0).toUpperCase()}</span>
                  <span className="nav-user-name">{user.email?.split('@')[0]}</span>
                  <svg className={`nav-chevron ${openMenu === 'user' ? 'open' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {openMenu === 'user' && (
                  <div className="nav-user-menu">
                    <button
                      onClick={() => handleNavigation('/profile')}
                      className="nav-user-item"
                    >
                      My Profile
                    </button>
                    <div className="nav-menu-divider"></div>
                    <button
                      onClick={handleLogout}
                      className="nav-user-item nav-logout"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-auth-buttons">
                <button
                  className="nav-btn nav-btn-outline"
                  onClick={() => handleNavigation('/login')}
                >
                  Login
                </button>
                <button
                  className="nav-btn nav-btn-primary"
                  onClick={() => handleNavigation('/signup')}
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
